<script setup>
import { message } from 'ant-design-vue'
import Header from '@/components/Header.vue'
import DesignFooter from '@/components/DesignFooter.vue'

const asset = (name) => `${import.meta.env.BASE_URL}image/omni-train-redesign/${name}`
const showComingSoon = () => message.info('Omni Train 工作台暂未开放，敬请期待。')
const stages = [
  { title: '准备数据', description: '上传样本，拆分任务，完成分类、检测与分割标注。', caption: '分类、检测与分割标注' },
  { title: '封装算法', description: '管理代码包、基础镜像、参数配置与构建记录。', caption: '基础镜像 · 参数配置' },
  { title: '启动训练', description: '组合数据集与算法，配置训练参数并运行任务。', caption: '数据集与算法' },
  { title: '观察结果', description: '持续查看日志、指标、产物与任务状态。', caption: '任务状态' },
  { title: '交付模型', description: '转换模型并面向目标硬件完成部署配置。', caption: '部署配置' },
]
const samples = [
  { image: 'helmet.webp', title: '安全帽检测' },
  { image: 'face.webp', title: '人脸检测' },
  { image: 'gesture.webp', title: '手势检测' },
  { image: 'segmentation.webp', title: '人像分割' },
]
</script>

<template>
  <Header design-always hide-banner />
  <main class="training-page">
    <section class="training-hero" aria-labelledby="training-title">
      <div class="training-container hero-layout">
        <div class="hero-copy">
          <h1 id="training-title">开放训练平台</h1>
          <p class="hero-description">从原始数据到可交付模型，每个环节保留上下文、版本与运行记录，让实验可以复现，让结果可以继续交付。</p>
          <button class="workspace-button" v-track="'omni_train > workspace'" type="button" @click="showComingSoon">敬请期待</button>
        </div>
        <div class="hero-art"><img :src="asset('hero.webp')" alt="Omni Train 开放训练平台主题示意" fetchpriority="high" /></div>
      </div>
    </section>

    <section class="training-section" aria-labelledby="engineering-title">
      <div class="training-container">
        <div class="section-heading">
          <h2 id="engineering-title">训练工程与模型产物</h2>
          <p>在工程中组织多次实验，关联数据集与算法，并持续追踪任务状态、训练日志和可下载产物。</p>
        </div>

        <figure class="workflow-figure" aria-label="从样本到部署模型的五阶段工作台示意，非实际操作界面">
          <ol class="workflow-panels">
            <li v-for="(stage, index) in stages" :key="stage.title" class="stage-panel">
              <div class="panel-title">{{ stage.title }}</div>
              <div v-if="index === 0" class="panel-body sample-grid">
                <img v-for="sample in samples" :key="sample.image" :src="asset(sample.image)" :alt="sample.title + '标注样本'" loading="lazy" />
              </div>
              <div v-else-if="index === 1" class="panel-body code-panel">
                <span>代码包</span><div class="code-lines" aria-hidden="true"><i v-for="n in 7" :key="n"></i></div>
              </div>
              <div v-else-if="index === 2" class="panel-body training-panel">
                <strong>训练参数</strong><div class="info-lines" aria-hidden="true"><i></i><i></i></div>
                <div class="mini-card"><span>运行任务</span><div class="progress-track" aria-hidden="true"><i></i></div></div>
              </div>
              <div v-else-if="index === 3" class="panel-body metrics-panel">
                <strong>日志、指标、产物</strong><img :src="asset('metrics.svg')" alt="训练指标变化曲线示意" loading="lazy" />
              </div>
              <div v-else class="panel-body delivery-panel">
                <div v-for="title in ['转换模型', '目标硬件']" :key="title" class="mini-card"><strong>{{ title }}</strong><div class="info-lines" aria-hidden="true"><i></i><i></i></div></div>
              </div>
              <p class="panel-caption">{{ stage.caption }}</p>
            </li>
          </ol>
        </figure>

        <ol class="stage-descriptions" aria-label="训练五阶段说明">
          <li v-for="stage in stages" :key="stage.title"><h3>{{ stage.title }}</h3><p>{{ stage.description }}</p></li>
        </ol>

        <section class="cli-section" aria-labelledby="cli-title">
          <div class="cli-copy"><h3 id="cli-title">OMNI TRAIN CLI</h3><p>网页工作台负责协作与可视化，CLI 支持本地研发与自动化，两种入口共享同一套资源。</p></div>
          <div class="cli-example"><code>omni-train context show --output json</code></div>
        </section>
      </div>
    </section>
  </main>
  <DesignFooter />
</template>

<style scoped>
.training-page { width: 100%; min-width: 0; color: #14233b; line-height: 1.5; }
.training-page, .training-page *, .training-page *::before, .training-page *::after { box-sizing: border-box; }
.training-page h1, .training-page h2, .training-page h3, .training-page p, .training-page ol, .training-page figure { margin: 0; }
.training-page img { display: block; max-width: 100%; }
.training-container { width: min(1440px, calc(100% - 48px)); margin: 0 auto; }
.training-hero { display: flex; align-items: center; min-height: 416px; padding: 48px 0; background: #f4f5f7; }
.hero-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: center; }
.hero-copy, .hero-art { min-width: 0; }
.hero-copy h1 { margin-top: 4px; font-size: 30px; font-weight: 600; line-height: 1.3; }
.hero-description { margin-top: 16px !important; color: #5c6c81; font-size: 16px; line-height: 1.75; }
.workspace-button { display: inline-flex; justify-content: center; align-items: center; min-height: 48px; padding: 12px 20px; margin-top: 32px; border: 0; border-radius: 4px; background: #ed000c; color: #fff; font: inherit; font-size: 16px; cursor: pointer; }
.workspace-button:hover { background: #cb000a; }
.workspace-button:focus-visible { outline: 2px solid #ed000c; outline-offset: 4px; }
.hero-art { position: relative; }
.hero-art img { width: 100%; height: auto; object-fit: contain; }
.hero-art::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(90deg, #f4f5f7, transparent 7%, transparent 93%, #f4f5f7), linear-gradient(180deg, #f4f5f7, transparent 8%, transparent 92%, #f4f5f7); }
.training-section { padding: 64px 0; }
.section-heading { margin-bottom: 32px; text-align: center; }
.section-heading h2 { font-size: 24px; font-weight: 600; line-height: 1.4; }
.section-heading p { margin-top: 16px; color: #617089; font-size: 16px; line-height: 24px; }
.workflow-figure { display: flex; align-items: center; min-height: 480px; padding: 48px clamp(24px, 5vw, 120px); border-radius: 12px; background: #f4f6fa; }
.workflow-panels { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 36px; width: 100%; padding: 0; list-style: none; }
.stage-panel { position: relative; display: flex; flex-direction: column; gap: 14px; min-width: 0; min-height: 288px; padding: 12px; border: 1px solid #e1e6ee; border-radius: 8px; background: #fff; box-shadow: 0 10px 24px rgb(33 48 71 / 9%); }
.stage-panel:not(:last-child)::after { content: '→'; position: absolute; top: 50%; right: -29px; transform: translateY(-50%); color: #ed000c; font-size: 24px; }
.panel-title { display: flex; align-items: center; gap: 8px; min-height: 24px; color: #17283f; font-size: 14px; font-weight: 600; }
.panel-title::before { content: ''; width: 4px; height: 16px; flex: 0 0 4px; border-radius: 2px; background: #ed000c; }
.panel-body { min-width: 0; min-height: 174px; padding: 12px; border-radius: 5px; background: #f6f8fb; font-size: 11px; color: #718096; }
.sample-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, 1fr); gap: 8px; padding: 8px; }
.sample-grid img { width: 100%; height: 100%; min-height: 0; aspect-ratio: 1; border-radius: 4px; object-fit: cover; }
.code-panel { background: #142135; color: #d5ddea; }
.code-lines { display: grid; gap: 12px; margin-top: 12px; }
.code-lines i { height: 4px; width: 80%; border-radius: 2px; background: #a8b6cb; }
.code-lines i:nth-child(even) { width: 60%; background: #627d9e; }
.code-lines i:nth-child(3) { width: 95%; background: #ef4055; }
.training-panel, .delivery-panel { display: flex; flex-direction: column; gap: 12px; }
.panel-body strong { color: #17283f; font-size: 12px; }
.info-lines { display: grid; gap: 8px; }
.info-lines i { display: block; width: 100%; height: 4px; border-radius: 2px; background: #d2dbe7; }
.info-lines i + i { width: 75%; }
.mini-card { display: flex; flex-direction: column; gap: 8px; padding: 10px; border: 1px solid #e1e6ee; border-radius: 8px; background: #fff; }
.progress-track { height: 5px; background: #e9eef5; }
.progress-track i { display: block; width: 72%; height: 100%; border-radius: 2px; background: #ed000c; }
.metrics-panel { display: flex; flex-direction: column; justify-content: space-between; gap: 10px; padding: 10px; }
.metrics-panel img { width: 100%; height: auto; }
.panel-caption { margin-top: auto !important; color: #6c7d93; font-size: 11px; line-height: 19px; }
.stage-descriptions { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 20px; padding: 0; margin-top: 24px !important; list-style: none; }
.stage-descriptions li { min-width: 0; padding: 16px; }
.training-page h3 { font-size: 18px; font-weight: 600; line-height: 28px; }
.stage-descriptions p { margin-top: 12px; color: #617089; font-size: 16px; line-height: 28px; }
.cli-section { display: grid; grid-template-columns: 1fr 1fr; align-items: center; gap: 48px; padding: 32px; margin-top: 32px; border-radius: 12px; background: #f4f7fb; }
.cli-copy, .cli-example { min-width: 0; }
.cli-copy p { margin-top: 16px; font-size: 16px; line-height: 24px; }
.cli-example { display: flex; align-items: center; min-height: 88px; padding: 24px; border-radius: 12px; background: #fff; }
.cli-example code { font: inherit; font-size: 16px; line-height: 24px; overflow-wrap: anywhere; }
@media (min-width: 768px) { .hero-copy h1 { font-size: 36px; } .hero-description { font-size: 18px; } .section-heading h2 { font-size: 26px; } }
@media (min-width: 992px) { .hero-copy h1 { font-size: 40px; } .section-heading h2 { font-size: 28px; } }
@media (min-width: 1200px) { .hero-copy h1 { font-size: 44px; } .section-heading h2 { font-size: 30px; } }
@media (min-width: 1400px) { .section-heading h2 { font-size: 32px; } }
@media (max-width: 1199px) { .workflow-panels { gap: 24px; } .stage-panel:not(:last-child)::after { right: -23px; font-size: 20px; } .workflow-figure { padding: 32px 24px; } .panel-body { padding: 8px; } }
@media (max-width: 991px) { .workflow-panels { grid-template-columns: repeat(2, minmax(0, 1fr)); } .stage-panel:not(:last-child)::after { display: none; } .sample-grid { grid-template-rows: repeat(2, 75px); } .sample-grid img { height: 75px; } .stage-descriptions { grid-template-columns: repeat(2, minmax(0, 1fr)); } .cli-section { gap: 24px; } }
@media (max-width: 767px) { .training-container { width: calc(100% - 32px); } .training-hero, .training-section { padding: 40px 0; } .hero-layout, .workflow-panels, .stage-descriptions, .cli-section { grid-template-columns: minmax(0, 1fr); } .hero-art { width: min(100%, 520px); margin: 8px auto 0; } .workflow-figure { padding: 24px 16px; } .stage-descriptions { gap: 8px; } .cli-section { padding: 24px; } .cli-example { padding: 16px; } }
</style>
